/**
 * API LAYER: Ball-by-Ball
 *
 * RESPONSIBILITY:
 * - Wrap backend HTTP endpoints
 * - No logic, no state mutation
 *
 * MUST NEVER:
 * - Transform responses
 * - Store state
 * - Contain cricket logic
 */

import api from "./axios";

export function verifyMatchReady(matchId) {
  return api.post(
    "/api/ballbyball/verify-match-is-ready-for-ball-by-ball-scoring/",
    {
      match_id: matchId,
    },
  );
}

export function initialiseSession(matchId) {
  return api.post("/api/ballbyball/initialise-ball-by-ball-session/", {
    match_id: matchId,
  });
}

export function loadSession(matchId) {
  return api.get("/api/ballbyball/initialise-ball-by-ball-session/", {
    params: { match_id: matchId },
  });
}

// 2026-06-10 — set which stadium end Over 1 was bowled from.
// Backend auto-derives every subsequent ball's bowling_end by alternation.
// `end` must be "A" or "B".
export function setFirstOverBowlingEnd(inningsId, end) {
  return api.post("/api/ballbyball/set-first-over-bowling-end/", {
    innings_id: inningsId,
    end,
  });
}

// 2026-06-11 — BBL Power Surge activation. Records the batting team's
// pick of the FIRST of the 2 consecutive Power Surge overs. Backend
// rejects when:
//   - match isn't T20_BBL,
//   - over_number < 11,
//   - the 2-over window would extend past the innings length,
//   - power_surge_start_over is already set to a different over.
export function startPowerSurge(inningsId, overNumber) {
  return api.post("/api/ballbyball/start-power-surge/", {
    innings_id: inningsId,
    over_number: overNumber,
  });
}

// 2026-06-22 — The Hundred strategic timeout. Records the ball_number
// AT which the batting side took its single ~2.5-minute break. Backend
// rejects when the match isn't HUNDRED or the innings already has a
// timeout recorded.
export function takeStrategicTimeout(inningsId, ballNumber) {
  return api.post("/api/ballbyball/take-strategic-timeout/", {
    innings_id: inningsId,
    ball_number: ballNumber,
  });
}

/* ===========================
   BALL ACTIONS
=========================== */

export function saveBall(matchId, ballPayload) {
  return api.post(
    "/api/ballbyball/record-single-ball-delivery/",
    {
      match_id: matchId,
      ...ballPayload,
    }
  );
}

export function undoBall(matchId) {
  return api.post(
    "/api/ballbyball/undo-most-recent-ball-delivery/",
    { match_id: matchId }
  );
}

// T141: bulk undo. n = number of balls to remove from the active
// innings, in [1, MAX_BATCH_UNDO=25]. Single rebuild on the server.
// Response carries `last_undone_balls` (newest-first array) for the
// redo stack, plus `undo_summary` with deleted_count + first/last
// over for the SPA preview.
export function undoLastNBalls(matchId, n) {
  return api.post(
    "/api/ballbyball/undo-last-n-balls/",
    { match_id: matchId, n },
  );
}

// T147: ghost-ball trash — 24h restore window for undone balls.
// listBallTrash returns the active (non-restored, non-expired)
// trash for a given match; restoreBallFromTrash replays the stored
// payload back through record-single-ball-delivery.
export function listBallTrash(matchId) {
  return api.get(`/api/ballbyball/trash/?match_id=${encodeURIComponent(matchId)}`);
}
export function restoreBallFromTrash(trashId) {
  return api.post(`/api/ballbyball/trash/${trashId}/restore/`);
}

export function endActiveInnings(matchId) {
  return api.post("/api/ballbyball/end-active-innings/", { match_id: matchId });
}

export function startNextInnings(matchId, payload = {}) {
  return api.post("/api/ballbyball/start-next-innings/", {
    match_id: matchId,
    ...payload,
  });
}

export function applyDls(matchId, inningsId, revisedTargetRuns, revisedMaxOvers) {
  return api.post("/api/ballbyball/apply-dls/", {
    match_id: matchId,
    innings_id: inningsId,
    revised_target_runs: revisedTargetRuns,
    revised_max_overs: revisedMaxOvers,
  });
}

export function applyPenalty(matchId, inningsId, runs, awardedTo, reason, reasonOther) {
  return api.post("/api/ballbyball/apply-penalty/", {
    match_id: matchId,
    innings_id: inningsId,
    runs,
    awarded_to: awardedTo,
    reason,
    reason_other: reasonOther,
  });
}

export function endMatch(matchId) {
  return api.post("/api/ballbyball/end-match/", { match_id: matchId });
}

// Test cricket — explicit declaration. Backend persists closure_type=DECLARED
// and ends the innings in one transactional call.
export function declareInnings(matchId) {
  return api.post("/api/ballbyball/declare-innings/", { match_id: matchId });
}

export function loadBallEntries(matchId) {
  return api.get("/api/ballbyball/match-ball-entries/", {
    params: { match_id: matchId },
  });
}

/* ===========================
   PAST-BALL EDIT
=========================== */

export function listInningsBalls(inningsId) {
  return api.get("/api/scoring/balls/", { params: { innings: inningsId } });
}

export function getBallForEdit(ballId) {
  return api.get(`/api/scoring/balls/${ballId}/`);
}

export function updateBall(ballId, ballPayload) {
  return api.patch(`/api/scoring/balls/${ballId}/edit/`, ballPayload);
}

/* ===========================
   PHANTOM-BALL STOP-GAP (2026-06-05)
   Irregular umpiring: end an over early (fewer than balls_per_over
   real balls) or extend past balls_per_over with non-statting ghost
   balls. See backend persist_phantom_ball.py for semantics.
=========================== */
export function endOverNow(inningsId) {
  return api.post(`/api/scoring/innings/${inningsId}/end-over-now/`);
}
export function addPhantomBall(inningsId) {
  return api.post(`/api/scoring/innings/${inningsId}/add-phantom-ball/`);
}

/* ===========================
   LAW EVENTS (Phase A/B/C)
=========================== */
export const listSubstitutions = (matchId) =>
  api.get(`/api/matches/${matchId}/substitutions/`);
export const createSubstitution = (matchId, payload) =>
  api.post(`/api/matches/${matchId}/substitutions/`, payload);
// XI + reserves per team, used by Impact-player / Concussion-sub dialogs
export const getMatchSquad = (matchId) =>
  api.get(`/api/matches/${matchId}/squad/`);

// 2026-07-08 — mid-match "fix scorer mistake" flow. Rewrites Ball /
// BallWicket rows that referenced the wrong XI player. Distinct from
// createSubstitution (concussion / impact / fielder / runner), which
// swaps from a specific ball onward without touching history.
// payload: { wrong_player_id, correct_player_id?, batting_position?,
//            is_captain?, is_wicket_keeper?, reason? }
export const correctPlayingXI = (matchId, teamId, payload) =>
  api.patch(
    `/api/matches/matches/${matchId}/playing-xi/${teamId}/correct/`,
    payload,
  );

export const listWarnings = (matchId) =>
  api.get(`/api/matches/${matchId}/warnings/`);
export const createWarning = (matchId, payload) =>
  api.post(`/api/matches/${matchId}/warnings/`, payload);

export const listPenalties = (matchId) =>
  api.get(`/api/matches/${matchId}/penalties/`);
export const createPenalty = (matchId, payload) =>
  api.post(`/api/matches/${matchId}/penalties/`, payload);

export const listConduct = (matchId) =>
  api.get(`/api/matches/${matchId}/conduct/`);
export const createConduct = (matchId, payload) =>
  api.post(`/api/matches/${matchId}/conduct/`, payload);

export const listBallEdits = (matchId) =>
  api.get(`/api/matches/${matchId}/ball-edits/`);

export const deleteEvent = (kind, eventId) =>
  api.delete(`/api/matches/events/${kind}/${eventId}/`);

/* ===========================
   COMMENTARY
=========================== */
export const listMatchCommentary = (matchId) =>
  api.get(`/api/matches/${matchId}/commentary/`);

export const regenerateBallCommentary = (ballId) =>
  api.post(`/api/scoring/balls/${ballId}/commentary/regenerate/`);
